import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { useCreatePostMailingCampaign } from '../../models/campaigns/mutations';
import { usePostMailingTemplates } from '../../models/postMailingTemplates/queries';

const CreatePostMailingCampaignModal = ({ isOpen, onClose }) => {
	const navigate = useNavigate();
	const [name, setName] = useState('');
	const [templateId, setTemplateId] = useState('');

	const { data: templates = [], isLoading: loadingTemplates } = usePostMailingTemplates();
	const createCampaign = useCreatePostMailingCampaign();

	const isSubmitting = createCampaign.isPending;
	const canSubmit = name.trim() && templateId && !isSubmitting;

	const resetAndClose = () => {
		setName('');
		setTemplateId('');
		onClose();
	};

	const handleCreate = async () => {
		if (!canSubmit) return;

		try {
			// The campaign links to the template via postMailingTemplateId; the design is
			// read live from the linked template (no snapshot copy).
			const created = await createCampaign.mutateAsync({
				name: name.trim(),
				postMailingTemplateId: templateId,
			});

			resetAndClose();
			if (created?.id) {
				navigate(`/post-mailing/${created.id}`);
			}
		} catch {
			// Error toast handled by the global MutationCache.
		}
	};

	const hasTemplates = templates.length > 0;

	return (
		<Modal isOpen={isOpen} onClose={resetAndClose} size="s">
			<Panel.Header onClose={resetAndClose}>Neue Post-Mailing Kampagne</Panel.Header>
			<Panel.Body>
				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-text mb-1">Kampagnenname</label>
						<TextInput
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="z.B. Frühjahrs-Mailing 2026"
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-text mb-1">Vorlage</label>
						{loadingTemplates ? (
							<div className="text-sm text-bgBase-800">Vorlagen werden geladen...</div>
						) : !hasTemplates ? (
							<div className="text-xs text-bgBase-800 italic">
								Keine Vorlagen vorhanden. Legen Sie zuerst unter „Vorlagen“ eine an.
							</div>
						) : (
							<Select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
								<option value="">Vorlage wählen...</option>
								{templates.map((tpl) => (
									<option key={tpl.id} value={tpl.id}>
										{tpl.name}
										{tpl.designData?.kind === 'pdf' ? ' (PDF)' : ''}
									</option>
								))}
							</Select>
						)}
						<p className="text-xs text-bgBase-800 mt-1">
							Die Kampagne verwendet das Design der gewählten Vorlage. Die Vorlage kann vor dem Versand
							noch gewechselt werden.
						</p>
					</div>
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={resetAndClose}>
					Abbrechen
				</Button>
				<Button variant="solid" onClick={handleCreate} disabled={!canSubmit} loading={isSubmitting}>
					Erstellen
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

CreatePostMailingCampaignModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default CreatePostMailingCampaignModal;
